import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { formatDate, formatPhone, getInitials } from 'utils/helpers';
import { SOURCE_COLORS, CONFIRM } from './helper';

const Item = ({ contact, revoked = false, confirming = false, onAction, onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const { name, phone, relationship, source, authorized_at, revoked_at } = contact?.attributes || {};
  const { message, cancel, confirm, wrapper, button } = CONFIRM[revoked ? 'reauthorize' : 'revoke'];

  const details = [relationship, phone && formatPhone(phone)].filter(Boolean).join(' · ');
  const since = authorized_at ? formatDate(authorized_at, 'medium') : '';
  const removedOn = revoked_at ? formatDate(revoked_at, 'medium') : '';

  const status = revoked
    ? ['Access removed', removedOn].filter(Boolean).join(' ')
    : ['Can discuss your care', since && `since ${since}`].filter(Boolean).join(' · ');

  const handleConfirm = async () => {
    setBusy(true);
    await onConfirm();
    setBusy(false);
  };

  return (
    <div className="flex flex-wrap items-start gap-4 py-4">
      <span className="relative shrink-0">
        <span className={`flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold ${revoked ? 'bg-secondary-100 text-secondary-400' : 'bg-blue-50 text-teamcare-blue'}`}>
          {getInitials(name || '')}
        </span>

        <span className={`absolute -bottom-0.5 -right-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full text-white ring-2 ring-white ${revoked ? 'bg-secondary-400' : 'bg-success-500'}`}>
          {revoked ? <X size={10} strokeWidth={3.5} /> : <Check size={10} strokeWidth={3.5} />}
        </span>
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className={`min-w-0 truncate text-[15px] font-bold ${revoked ? 'text-secondary-500' : 'text-secondary-900'}`}>{name}</p>

          {source && (
            <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${revoked ? 'bg-gray-100 text-secondary-400' : SOURCE_COLORS[source]}`}>
              {source}
            </span>
          )}
        </div>

        {details && <p className={`mt-0.5 truncate text-sm ${revoked ? 'text-secondary-300' : 'text-secondary-400'}`}>{details}</p>}

        <p className={`mt-1 text-sm font-semibold ${revoked ? 'text-secondary-400' : 'text-success-700'}`}>{status}</p>
      </div>

      {!confirming && (
        <div className="basis-full pl-16 sm:basis-auto sm:self-center sm:pl-0">
          <button
            type="button"
            onClick={onAction}
            className={`btn btn-sm h-10 min-h-0 rounded-xl border-gray-200 bg-white px-4 font-semibold ${
              revoked ? 'text-success-700 hover:border-success-200 hover:bg-success-50' : 'text-error hover:border-red-200 hover:bg-red-50'
            }`}
          >
            {revoked ? 'Re-authorize' : 'Revoke'}
          </button>
        </div>
      )}

      {confirming && (
        <div className={`flex basis-full flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${wrapper}`}>
          <p className="basis-full text-sm text-secondary-800 sm:min-w-0 sm:flex-1 sm:basis-auto">{message((name || '').split(' ')[0])}</p>

          <div className="flex basis-full flex-col gap-2 sm:basis-auto sm:flex-row">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="btn h-10 min-h-0 rounded-xl border-gray-200 bg-white px-4 font-semibold text-secondary-800 hover:bg-gray-50 disabled:bg-white disabled:opacity-60"
            >
              {cancel}
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              className={`btn h-10 min-h-0 rounded-xl border-none px-4 font-semibold text-white disabled:text-white disabled:opacity-60 ${button}`}
            >
              {busy ? <span className="loading loading-spinner loading-xs" /> : confirm}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Item;
